import { COURSE_STRUCTURES, COURSE_TYPES, STATUSES, USER_ROLES } from 'constants/common';

import _orderBy from 'lodash/orderBy';

import * as api from 'api/methods';

export type LinkedSchool = {
  id: string;
  name: string;
  preview?: string;
};

export type LinkedCourse = {
  id: string;
  title: string;
  type: string;
  structure?: string;
  moduleId?: string;
  moduleTitle?: string;
  order?: number;
  moduleOrder?: number;
  preview?: string;
  school?: LinkedSchool;
};

export type CourseModuleOption = {
  id: string;
  title: string;
  order?: number;
};

export const isModularCourse = (course: LinkedCourse): boolean =>
  course.structure === COURSE_STRUCTURES.MODULES;

export type CourseLessonRow = {
  id: string;
  title: string;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  courses: LinkedCourse[];
  schools: LinkedSchool[];
};

type PaginatedLessonsResponse = {
  data: CourseLessonRow[];
  currentPage: number;
  totalPages: number | null;
  perPage: number;
};

// Primary GET /lessons?role=COURSE returns all types; fallback iterates all five.
const ALL_COURSE_TYPES = [
  COURSE_TYPES.COURSE,
  COURSE_TYPES.TRAINING,
  COURSE_TYPES.ARCADE,
  COURSE_TYPES.DAILY_CHALLENGE,
  COURSE_TYPES.LEARNING_PATH,
];

const EMPTY_PAGINATED_RESPONSE: PaginatedLessonsResponse = {
  data: [],
  currentPage: 1,
  totalPages: 1,
  perPage: 100,
};

const _mapLinkedSchool = (school: any): LinkedSchool | undefined => {
  if (!school?.id && !school?._id) {
    return undefined;
  }

  return {
    id: school.id ?? school._id,
    name: school.name,
    preview: school.preview,
  };
};

export const deriveSchoolsFromCourses = (
  courses: LinkedCourse[],
): LinkedSchool[] => {
  const schoolsById = new Map<string, LinkedSchool>();

  for (const course of courses) {
    if (!course.school?.id || schoolsById.has(course.school.id)) {
      continue;
    }

    schoolsById.set(course.school.id, course.school);
  }

  return [...schoolsById.values()];
};

const _mapLinkedSchools = (schools: any[] | undefined): LinkedSchool[] => {
  if (!Array.isArray(schools)) {
    return [];
  }

  return schools
    .map(_mapLinkedSchool)
    .filter((school): school is LinkedSchool => Boolean(school));
};

const _hasCourseLinkage = (lesson: any): boolean =>
  Boolean(
    lesson?.courseId ||
      lesson?.course?.id ||
      (Array.isArray(lesson?.courses) && lesson.courses.length),
  );

const _extractCoursesFromLesson = (
  lesson: any,
  courseTitleById: Record<string, string> = {},
): LinkedCourse[] => {
  if (Array.isArray(lesson.courses) && lesson.courses.length) {
    return lesson.courses.map((course: any) => ({
      id: course.id,
      title: course.title,
      type: course.type,
      structure: course.structure,
      moduleId: course.moduleId,
      moduleTitle: course.moduleTitle,
      order: course.order,
      moduleOrder: course.moduleOrder,
      preview: course.preview,
      school: _mapLinkedSchool(course.school),
    }));
  }

  if (lesson.course?.id) {
    return [
      {
        id: lesson.course.id,
        title: lesson.course.title,
        type: lesson.course.type,
        preview: lesson.course.preview,
        school: _mapLinkedSchool(lesson.course.school),
      },
    ];
  }

  if (lesson.courseId) {
    return [
      {
        id: lesson.courseId,
        title: courseTitleById[lesson.courseId] || lesson.courseId,
        type: COURSE_TYPES.COURSE,
        preview: undefined,
      },
    ];
  }

  return [];
};

const _normalizeLessonsResponse = (rawLessons: unknown): any[] => {
  if (Array.isArray(rawLessons)) {
    return rawLessons;
  }

  if (
    rawLessons &&
    typeof rawLessons === 'object' &&
    Array.isArray((rawLessons as { data?: unknown }).data)
  ) {
    return (rawLessons as { data: any[] }).data;
  }

  return [];
};

export type LessonOrderOption = { label: string; value: string };

const _fetchScopedLessonsForCourse = async (
  courseId: string,
  moduleId?: string,
): Promise<any[]> => {
  const rawLessons = await api.getCourseLessons(courseId);
  const lessons = _normalizeLessonsResponse(rawLessons);

  return moduleId
    ? lessons.filter((lesson: any) => lesson.moduleId === moduleId)
    : lessons;
};

export const fetchLessonOrderOptionsForCourse = async (
  courseId: string,
  moduleId?: string,
): Promise<LessonOrderOption[]> => {
  const scopedLessons = await _fetchScopedLessonsForCourse(courseId, moduleId);
  const maxPosition = Math.max(scopedLessons.length + 1, 1);

  return Array.from({ length: maxPosition }, (_, index) => {
    const position = String(index + 1);

    return {
      label: position,
      value: position,
    };
  });
};

export const fetchDefaultLessonOrderForCourse = async (
  courseId: string,
  moduleId?: string,
): Promise<number> => {
  const scopedLessons = await _fetchScopedLessonsForCourse(courseId, moduleId);

  const orders = scopedLessons
    .map((lesson: any) => lesson.order)
    .filter(
      (order: unknown): order is number =>
        typeof order === 'number' && Number.isInteger(order) && order >= 1,
    );

  if (!orders.length) {
    return 1;
  }

  return Math.max(...orders) + 1;
};

const _normalizeModulesResponse = (rawModules: unknown): any[] => {
  if (Array.isArray(rawModules)) {
    return rawModules;
  }

  if (
    rawModules &&
    typeof rawModules === 'object' &&
    Array.isArray((rawModules as { data?: unknown }).data)
  ) {
    return (rawModules as { data: any[] }).data;
  }

  return [];
};

const _mapModulesToOptions = (modules: any[]): CourseModuleOption[] => {
  const sortedModules = modules.some((module) => module.order != null)
    ? _orderBy(modules, ['order'], ['asc'])
    : modules;

  return sortedModules.map((module) => ({
    id: module.id ?? module._id,
    title: module.title,
    order: module.order ?? 0,
  }));
};

export const fetchModulesForCourse = async (
  course: LinkedCourse,
): Promise<CourseModuleOption[]> => {
  let rawModules: unknown;

  switch (course.type) {
    case COURSE_TYPES.TRAINING:
      rawModules = await api.getTrainingModules(course.id);
      break;
    case COURSE_TYPES.ARCADE:
      rawModules = await api.getArcadeModules(course.id);
      break;
    default:
      rawModules = await api.getCourseModules(course.id);
      break;
  }

  return _mapModulesToOptions(_normalizeModulesResponse(rawModules));
};

const _buildModuleMetaByCourseId = async (
  courses: LinkedCourse[],
): Promise<Map<string, Map<string, { title: string; order: number }>>> => {
  const moduleMetaByCourseId = new Map<
    string,
    Map<string, { title: string; order: number }>
  >();
  const modularCourses = courses.filter(isModularCourse);

  await Promise.all(
    modularCourses.map(async (course) => {
      try {
        const modules = await fetchModulesForCourse(course);

        moduleMetaByCourseId.set(
          course.id,
          new Map(
            modules.map((module) => [
              module.id,
              { title: module.title, order: module.order ?? 0 },
            ]),
          ),
        );
      } catch {
        // Skip courses that fail to load modules.
      }
    }),
  );

  return moduleMetaByCourseId;
};

const _enrichCourseWithModulePlacement = (
  course: LinkedCourse,
  lesson: { moduleId?: string | null; order?: number },
  moduleMetaByCourseId: Map<string, Map<string, { title: string; order: number }>>,
): LinkedCourse => {
  const enrichedCourse: LinkedCourse = {
    ...course,
    ...(typeof lesson.order === 'number' ? { order: lesson.order } : {}),
  };

  if (!isModularCourse(course) || !lesson.moduleId) {
    return enrichedCourse;
  }

  const moduleMeta = moduleMetaByCourseId.get(course.id)?.get(lesson.moduleId);

  return {
    ...enrichedCourse,
    moduleId: lesson.moduleId,
    ...(moduleMeta?.title ? { moduleTitle: moduleMeta.title } : {}),
    ...(moduleMeta?.order != null ? { moduleOrder: moduleMeta.order } : {}),
  };
};

const _mergeLessonRows = (
  rowsMap: Map<string, CourseLessonRow>,
  lesson: any,
  course: LinkedCourse,
) => {
  const existingRow = rowsMap.get(lesson.id);

  if (existingRow) {
    if (!existingRow.courses.some((linkedCourse) => linkedCourse.id === course.id)) {
      existingRow.courses.push(course);
      existingRow.schools = deriveSchoolsFromCourses(existingRow.courses);
    }

    if (
      lesson.updatedAt &&
      (!existingRow.updatedAt ||
        new Date(lesson.updatedAt) > new Date(existingRow.updatedAt))
    ) {
      existingRow.updatedAt = lesson.updatedAt;
    }

    return;
  }

  rowsMap.set(lesson.id, {
    id: lesson.id,
    title: lesson.title,
    type: lesson.type,
    status: lesson.status,
    createdAt: lesson.createdAt,
    updatedAt: lesson.updatedAt ?? lesson.createdAt ?? '',
    courses: [course],
    schools: deriveSchoolsFromCourses([course]),
  });
};

type FetchCoursesByTypeOptions = {
  excludeArchived?: boolean;
  schoolId?: string;
};

const _mapCourseList = (
  rawCourses: any[] | undefined,
  courseType: string,
  excludeArchived?: boolean,
): LinkedCourse[] => {
  const courses = rawCourses || [];
  const filteredCourses = excludeArchived
    ? courses.filter((course) => course.status !== STATUSES.ARCHIVED)
    : courses;

  return filteredCourses.map((course) => ({
    id: course.id,
    title: course.title,
    type: course.type || courseType,
    structure: course.structure,
    preview: course.preview,
    school: _mapLinkedSchool(course.school),
  }));
};

const _fetchCoursesByType = async (
  courseType: string,
  getState: () => any,
  options?: FetchCoursesByTypeOptions,
): Promise<LinkedCourse[]> => {
  const state = getState();
  const user = state.auth.user;
  const isRootAdmin = user.role === USER_ROLES.ROOT_ADMIN;
  const scopedSchoolId = !isRootAdmin
    ? options?.schoolId ?? user.schoolId
    : undefined;
  const listParams: Record<string, unknown> = { limit: 1000, page: 1 };

  if (options?.excludeArchived) {
    listParams.excludeStatus = STATUSES.ARCHIVED;
  }

  if (scopedSchoolId) {
    switch (courseType) {
      case COURSE_TYPES.TRAINING: {
        const response = await api.getSchoolTrainings({
          schoolId: scopedSchoolId,
          params: listParams,
        });

        return _mapCourseList(
          response.data,
          courseType,
          options?.excludeArchived,
        );
      }
      case COURSE_TYPES.ARCADE: {
        const response = await api.getSchoolArcades({
          schoolId: scopedSchoolId,
          params: listParams,
        });

        return _mapCourseList(
          response.data,
          courseType,
          options?.excludeArchived,
        );
      }
      case COURSE_TYPES.DAILY_CHALLENGE: {
        const response = await api.getSchoolDailyChallenges({
          schoolId: scopedSchoolId,
          params: listParams,
        });

        return _mapCourseList(
          response.data,
          courseType,
          options?.excludeArchived,
        );
      }
      default: {
        const response = await api.getSchoolCourses({
          schoolId: scopedSchoolId,
          params: { ...listParams, type: courseType },
        });

        return _mapCourseList(
          response.data,
          courseType,
          options?.excludeArchived,
        );
      }
    }
  }

  switch (courseType) {
    case COURSE_TYPES.TRAINING: {
      const response = await api.getTrainings(listParams);

      return _mapCourseList(
        response.data,
        courseType,
        options?.excludeArchived,
      );
    }
    case COURSE_TYPES.ARCADE: {
      const response = await api.getArcades(listParams);

      return _mapCourseList(
        response.data,
        courseType,
        options?.excludeArchived,
      );
    }
    case COURSE_TYPES.DAILY_CHALLENGE: {
      const response = await api.getDailyChallenges(listParams);

      return _mapCourseList(
        response.data,
        courseType,
        options?.excludeArchived,
      );
    }
    default: {
      const response = await api.getCourses({ ...listParams, type: courseType });

      return _mapCourseList(
        response.data,
        courseType,
        options?.excludeArchived,
      );
    }
  }
};

export const fetchCoursesForSchool = async (
  schoolId: string,
  getState: () => any,
  options?: Omit<FetchCoursesByTypeOptions, 'schoolId'>,
): Promise<LinkedCourse[]> => {
  const isRootAdmin = getState()?.auth?.user?.role === USER_ROLES.ROOT_ADMIN;
  const fetchOptions = isRootAdmin ? options : { ...options, schoolId };

  const coursesByType = await Promise.all(
    ALL_COURSE_TYPES.map((courseType) =>
      _fetchCoursesByType(courseType, getState, fetchOptions),
    ),
  );

  let result = coursesByType.flat();

  if (isRootAdmin) {
    result = result.filter((course) => course.school?.id === schoolId);
  }

  return result.sort((leftCourse, rightCourse) =>
    leftCourse.title.localeCompare(rightCourse.title, undefined, {
      sensitivity: 'base',
    }),
  );
};

const _getLessonsForCourse = async (course: LinkedCourse) =>
  api.getCourseLessons(course.id);

const _excludeRowsByStatus = (
  rows: CourseLessonRow[],
  excludeStatus?: string,
): CourseLessonRow[] => {
  if (!excludeStatus) {
    return rows;
  }

  const normalizedExcludeStatus = excludeStatus.toUpperCase();

  return rows.filter(
    (row) => (row.status || '').toUpperCase() !== normalizedExcludeStatus,
  );
};

const _shouldExcludeLessonByStatus = (
  lesson: { status?: string },
  excludeStatus?: string,
): boolean =>
  Boolean(
    excludeStatus &&
      (lesson.status || '').toUpperCase() === excludeStatus.toUpperCase(),
  );

const _parseAllowedCourseIds = (
  courseIds?: string,
): Set<string> | null => {
  if (!courseIds?.trim()) {
    return null;
  }

  const parsedCourseIds = courseIds
    .split(',')
    .map((courseId) => courseId.trim())
    .filter(Boolean);

  return parsedCourseIds.length ? new Set(parsedCourseIds) : null;
};

const _filterRowsByCourseIds = (
  rows: CourseLessonRow[],
  courseIds?: string,
): CourseLessonRow[] => {
  const allowedCourseIds = _parseAllowedCourseIds(courseIds);

  if (!allowedCourseIds?.size) {
    return rows;
  }

  return rows.filter((row) =>
    row.courses.some((course) => allowedCourseIds.has(course.id)),
  );
};

const _filterRowsByTitleQuery = (
  rows: CourseLessonRow[],
  query?: string,
): CourseLessonRow[] => {
  const trimmedQuery = query?.trim();

  if (!trimmedQuery) {
    return rows;
  }

  const normalizedQuery = trimmedQuery.toLowerCase();

  return rows.filter((row) =>
    (row.title || '').toLowerCase().includes(normalizedQuery),
  );
};

export const fetchAllCoursesForLessonAttach = async (
  getState: () => any,
): Promise<LinkedCourse[]> => {
  const coursesByType = await Promise.all(
    ALL_COURSE_TYPES.map((courseType) =>
      _fetchCoursesByType(courseType, getState, { excludeArchived: true }),
    ),
  );

  return coursesByType
    .flat()
    .sort((leftCourse, rightCourse) =>
      leftCourse.title.localeCompare(rightCourse.title, undefined, {
        sensitivity: 'base',
      }),
    );
};

export const fetchCoursesLessonsMerged = async (
  params: Record<string, any> = {},
  getState: () => any,
): Promise<PaginatedLessonsResponse> => {
  try {
    const allowedCourseIds = _parseAllowedCourseIds(params.courseIds);
    let courses: LinkedCourse[];

    if (params.schoolId) {
      const schoolCourses = await fetchCoursesForSchool(params.schoolId, getState);
      courses = allowedCourseIds?.size
        ? schoolCourses.filter((course) => allowedCourseIds.has(course.id))
        : schoolCourses;
    } else {
      const coursesByType = await Promise.all(
        ALL_COURSE_TYPES.map((courseType) =>
          _fetchCoursesByType(courseType, getState),
        ),
      );
      courses = coursesByType.flat();

      if (allowedCourseIds?.size) {
        courses = courses.filter((course) => allowedCourseIds.has(course.id));
      }
    }

    const rowsMap = new Map<string, CourseLessonRow>();
    const moduleMetaByCourseId = await _buildModuleMetaByCourseId(courses);

    await Promise.all(
      courses.map(async (course) => {
        try {
          const lessonsResponse = await _getLessonsForCourse(course);
          const lessonList = Array.isArray(lessonsResponse)
            ? lessonsResponse
            : lessonsResponse?.data ?? [];

          for (const lesson of lessonList) {
            if (_shouldExcludeLessonByStatus(lesson, params.excludeStatus)) {
              continue;
            }

            _mergeLessonRows(
              rowsMap,
              lesson,
              _enrichCourseWithModulePlacement(
                course,
                lesson,
                moduleMetaByCourseId,
              ),
            );
          }
        } catch {
          // Skip courses that fail to load lessons.
        }
      }),
    );

    const data = _filterRowsByCourseIds(
      _filterRowsByTitleQuery(
        _excludeRowsByStatus(Array.from(rowsMap.values()), params.excludeStatus),
        params.query,
      ),
      params.courseIds,
    );

    return {
      data,
      currentPage: 1,
      totalPages: 1,
      perPage: data.length || 100,
    };
  } catch {
    return EMPTY_PAGINATED_RESPONSE;
  }
};

export const fetchCoursesLessonsFromApi = async (
  params: Record<string, any>,
  getState: () => any,
): Promise<PaginatedLessonsResponse> => {
  try {
    const response = await api.getLessons({ role: 'COURSE', ...params });
    const lessons = response?.data;

    if (!Array.isArray(lessons) || !lessons.length || !_hasCourseLinkage(lessons[0])) {
      return fetchCoursesLessonsMerged(params, getState);
    }

    const needsCourseTitles = lessons.some(
      (lesson: any) =>
        lesson.courseId && !lesson.course?.title && !Array.isArray(lesson.courses),
    );

    let courseTitleById: Record<string, string> = {};

    if (needsCourseTitles) {
      const coursesForTitles = params.schoolId
        ? await fetchCoursesForSchool(params.schoolId, getState)
        : (
            await Promise.all(
              ALL_COURSE_TYPES.map((courseType) =>
                _fetchCoursesByType(courseType, getState),
              ),
            )
          ).flat();

      courseTitleById = Object.fromEntries(
        coursesForTitles.map((course) => [course.id, course.title]),
      );
    }

    const data = _filterRowsByCourseIds(
      _excludeRowsByStatus(
        lessons.map((lesson: any) => {
          const courses = _extractCoursesFromLesson(lesson, courseTitleById);
          const schools = _mapLinkedSchools(lesson.schools);

          return {
            id: lesson.id,
            title: lesson.title,
            type: lesson.type,
            status: lesson.status,
            createdAt: lesson.createdAt,
            updatedAt: lesson.updatedAt ?? lesson.createdAt ?? '',
            courses,
            schools: schools.length ? schools : deriveSchoolsFromCourses(courses),
          };
        }),
        params.excludeStatus,
      ),
      params.courseIds,
    );

    return {
      data,
      currentPage: response.currentPage ?? 1,
      totalPages: response.totalPages ?? 1,
      perPage: response.perPage ?? params.limit ?? 100,
    };
  } catch {
    return fetchCoursesLessonsMerged(params, getState);
  }
};
